import type { Character } from '../domain/character.ts';
import type { RuntimeState } from '../domain/runtime.ts';
import type { ExpirationCondition } from '../domain/encounter.ts';
import type { Item, ItemPowerDuration } from '../content/types.ts';
import type { ConditionContent } from '../content/conditions.ts';
import type { InventoryCatalogue } from '../rules/inventory.ts';
import type { Effect } from '../rules/effects.ts';
import type { Roller } from '../rules/dice.ts';
import { parseDice, rollDice } from '../rules/damageDice.ts';
import { addTimedEffect } from '../rules/encounter.ts';
import { ROUND_SECONDS, describeElapsed } from '../rules/gameTime.ts';
import { planItemPower } from '../rules/itemPower.ts';
import { STAT_LABELS } from '../domain/stats.ts';
import { buildEffectiveCharacter } from './effectiveCharacter.ts';
import { deriveStats } from './derivedStats.ts';
import { spendEncounterAction } from './encounterService.ts';
import { healCharacterSlots, restoreCharacterMana } from './healthService.ts';
import { inflictPotionSickness, restartPotionCadence } from './itemUseService.ts';

/**
 * Activating an Item Power: what changes on the crawler.
 *
 * PROJECT RULING - HOMEBREW (docs/RULES_CONFLICTS.md OPEN-018).
 *
 * DOES NOT TOUCH CHARGES OR COOLDOWN. Those belong to the Item and are spent by
 * the table's server in the same transaction that saves what this returns
 * (`player.activateItem`). This function answers only "given that the Item
 * fires, what happens to this crawler", so it is the same whether the Item has
 * three charges or none.
 */

export interface ItemPowerDeps {
  catalogue: InventoryCatalogue;
  conditions: ConditionContent;
  /** The Effects in play, for Max Mana, the Health Bar and Stat modifiers. */
  effects: readonly Effect[];
  roller: Roller;
  newId: () => string;
}

export interface ItemPowerDamage {
  dice: string;
  faces: number[];
  stat: string | null;
  statMod: number;
  total: number;
  damageType: string;
}

export type ItemPowerOutcome =
  | {
      ok: true;
      character: Character;
      runtime: RuntimeState;
      lines: string[];
      damage: ItemPowerDamage[];
      consumed: boolean;
      potionSickness: boolean;
    }
  | { ok: false; reason: string };

function expiry(duration: ItemPowerDuration): ExpirationCondition[] {
  switch (duration.kind) {
    case 'rounds':
      // Both clocks, because a Buff started in a corridor must still end: the
      // round counter only moves in a fight, elapsed time moves everywhere,
      // and an Effect ends on whichever comes first (Rules p.44).
      return [
        { kind: 'after-rounds', roundsRemaining: duration.rounds },
        { kind: 'elapsed-time', seconds: duration.rounds * ROUND_SECONDS },
      ];
    case 'seconds':
      return [{ kind: 'elapsed-time', seconds: duration.seconds }];
    case 'encounter':
      return [{ kind: 'end-of-encounter' }];
  }
}

function describeDuration(duration: ItemPowerDuration): string {
  switch (duration.kind) {
    case 'rounds':
      return `${duration.rounds} round${duration.rounds === 1 ? '' : 's'}`;
    case 'seconds':
      return describeElapsed(duration.seconds);
    case 'encounter':
      return 'the rest of the fight';
  }
}

export function activateItemPower(
  character: Character,
  runtime: RuntimeState,
  itemId: string,
  deps: ItemPowerDeps,
): ItemPowerOutcome {
  if (runtime.characterId !== character.id) {
    return {
      ok: false,
      reason:
        `Internal error: this Item use is for ${character.id} but the runtime supplied belongs ` +
        `to ${runtime.characterId}. Nothing has been changed. Reopen the crawler and try again.`,
    };
  }

  const item: Item | undefined = deps.catalogue.lookupItem(itemId);
  const plan = planItemPower(item, character, runtime);
  if (!plan.canUse || item === undefined || item.power === undefined) {
    return { ok: false, reason: plan.refusal ?? 'That Item cannot be used.' };
  }
  const power = item.power;
  const lookupItem = (id: string) => deps.catalogue.lookupItem(id);
  const lines: string[] = [];
  const damage: ItemPowerDamage[] = [];
  let next = character;
  let nextRuntime = runtime;

  // 1. The copy that is used up, taken from wherever it physically was.
  if (plan.consumedFrom !== null) {
    const from = plan.consumedFrom;
    next = {
      ...next,
      inventory: next.inventory
        .map((entry) =>
          entry.itemId === itemId
            ? {
                ...entry,
                quantity: entry.quantity - 1,
                ...(from === 'held' ? { held: entry.held - 1 } : {}),
                ...(from === 'equipped' ? { equipped: entry.equipped - 1 } : {}),
              }
            : entry,
        )
        .filter((entry) => entry.quantity > 0),
    };
    if (from === 'hotlist') {
      let taken = false;
      next = {
        ...next,
        hotlist: next.hotlist.map((slot) => {
          if (taken || slot === null || slot === undefined || slot.kind !== 'item' || slot.itemId !== itemId) return slot;
          taken = true;
          return slot.quantity <= 1 ? null : { kind: 'item', itemId, quantity: slot.quantity - 1 };
        }),
      };
    }
  }

  // 2. What it does, in the order written.
  power.effects.forEach((effect, index) => {
    switch (effect.kind) {
      case 'heal-slots': {
        const before = next.healthBarSlotsRemaining;
        next = healCharacterSlots(next, effect.slots, deps.effects, lookupItem);
        const healed = next.healthBarSlotsRemaining - before;
        lines.push(
          healed > 0
            ? `${power.name} healed ${healed} Health Bar slot${healed === 1 ? '' : 's'} (now ${next.healthBarSlotsRemaining}).`
            : `${power.name}: the Health Bar is already full.`,
        );
        break;
      }
      case 'restore-mana': {
        const before = next.currentMana;
        const max = deriveStats(next, deps.effects, lookupItem).mana.max;
        next = restoreCharacterMana(next, effect.amount === 'full' ? max : effect.amount, deps.effects, lookupItem);
        const restored = next.currentMana - before;
        lines.push(
          restored > 0
            ? `${power.name} restored ${restored} Mana (now ${next.currentMana}/${max}).`
            : `${power.name}: Mana is already full.`,
        );
        break;
      }
      case 'damage': {
        const roll = rollDice(deps.roller, parseDice(effect.dice));
        const statMod = effect.stat === null
          ? 0
          : buildEffectiveCharacter(character, deps.effects, lookupItem).statMod(effect.stat);
        const total = Math.max(0, roll.total + statMod);
        damage.push({ dice: effect.dice, faces: roll.faces, stat: effect.stat, statMod, total, damageType: effect.damageType });
        const statText = effect.stat === null ? '' : ` ${statMod >= 0 ? '+' : '-'} ${Math.abs(statMod)} ${STAT_LABELS[effect.stat].abbr}`;
        lines.push(`${power.name}: ${total} ${effect.damageType} damage (${effect.dice} [${roll.faces.join(', ')}]${statText}). Tell your GM.`);
        break;
      }
      case 'buff': {
        nextRuntime = addTimedEffect(nextRuntime, {
          effect: {
            // One id per Item and effect, so using it again REFRESHES the Buff
            // (`addTimedEffect`) instead of stacking a second copy.
            id: `power:${item.id}:${index}`,
            sourceId: item.id,
            sourceKind: 'buff',
            label: effect.label,
            modifiers: effect.modifiers.map((modifier) => ({ ...modifier })),
          },
          endsWhen: expiry(effect.duration),
        });
        lines.push(`${effect.label} for ${describeDuration(effect.duration)}.`);
        break;
      }
    }
  });

  // 3. Potion rules, for a Power that is a Potion (Rules p.49).
  if (plan.causesPotionSickness) {
    const sick = inflictPotionSickness(nextRuntime, deps);
    if (!sick.ok) return { ok: false, reason: sick.reason };
    nextRuntime = sick.runtime;
    lines.push(sick.line);
  }

  // 4. The Action, through the shared pathway so `after-next-action` expiry
  // fires exactly as it does for every other Action.
  if (plan.spendsAction) {
    const spent = spendEncounterAction(nextRuntime, character.id, 1, 'normal');
    if (!spent.ok) return { ok: false, reason: spent.reason };
    nextRuntime = spent.runtime;
    for (const id of spent.expiredEffectIds) lines.push(`${id} ended with that Action`);
    if (item.kind === 'potion' && power.consumes) nextRuntime = restartPotionCadence(nextRuntime);
  }

  if (lines.length === 0) lines.push(`${power.name} used.`);
  return {
    ok: true,
    character: next,
    runtime: nextRuntime,
    lines,
    damage,
    consumed: plan.consumedFrom !== null,
    potionSickness: plan.causesPotionSickness,
  };
}
